import { SCORABLE_SENIORITY_LEVELS } from "@signalist/shared-types";

import {
	Tier,
	TIERS,
} from "@/pages/Private/pages/OnboardingTour/components/steps/scoring/criteriaTiers";

import { ScoreCriteriaCriterion, ScoreCriteriaType } from "../../schema/score.schema";
import { tempId } from "../../shell/configDraft";
import { isCriterionGroupActive } from "../../shell/criterionActive";

/**
 * Pure edits on the lead scorings' working copy (PREC-4055): create, duplicate,
 * rename, activate, the seniority ladder, AI criteria in their two shapes, and
 * copying grades between scorings. The list, location and size criteria are
 * edited through the tour's tier helpers; this module owns everything else.
 */

export type Section = "companyScoring" | "personaScoring";

export type FixedCriterionKey =
	| "HQ_COUNTRY"
	| "SUBSIDIARY_COUNTRY"
	| "INDUSTRY"
	| "EMPLOYEE_SIZE"
	| "PERSON_LOCATION"
	| "JOB_LEVEL";

/** One criterion the editor can select: a fixed group or one AI criterion by position. */
export interface CriterionRef {
	section: Section;
	key: FixedCriterionKey | "OTHER";
	/** Position inside `OTHER`, for AI criteria. */
	index?: number;
}

export const criterionRefKey = (ref: CriterionRef): string =>
	ref.key === "OTHER" ? `${ref.section}:OTHER:${ref.index}` : ref.key;

const FIXED_ORDER: CriterionRef[] = [
	{ section: "companyScoring", key: "HQ_COUNTRY" },
	{ section: "companyScoring", key: "SUBSIDIARY_COUNTRY" },
	{ section: "companyScoring", key: "INDUSTRY" },
	{ section: "companyScoring", key: "EMPLOYEE_SIZE" },
	{ section: "personaScoring", key: "PERSON_LOCATION" },
	{ section: "personaScoring", key: "JOB_LEVEL" },
];

/** Every criterion of a scoring in display order: the six fixed ones, then the AI criteria. */
export const listCriteria = (scoring: ScoreCriteriaType): CriterionRef[] => [
	...FIXED_ORDER,
	...scoring.criteria.companyScoring.OTHER.map((_, index) => ({
		section: "companyScoring" as const,
		key: "OTHER" as const,
		index,
	})),
	...scoring.criteria.personaScoring.OTHER.map((_, index) => ({
		section: "personaScoring" as const,
		key: "OTHER" as const,
		index,
	})),
];

type AiCriterion = ScoreCriteriaCriterion["companyScoring"]["OTHER"][number];

export const aiCriterionOf = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef
): AiCriterion | undefined =>
	ref.key === "OTHER" ? scoring.criteria[ref.section].OTHER[ref.index ?? -1] : undefined;

interface Group {
	active?: boolean;
	ifNotFound?: string;
	items?: unknown[];
}

const groupOf = (scoring: ScoreCriteriaType, ref: CriterionRef): Group | undefined =>
	ref.key === "OTHER"
		? aiCriterionOf(scoring, ref)
		: (scoring.criteria[ref.section] as unknown as Record<string, Group>)[ref.key];

export const isCriterionOn = (scoring: ScoreCriteriaType, ref: CriterionRef): boolean =>
	isCriterionGroupActive(groupOf(scoring, ref));

export const ifNotFoundOf = (scoring: ScoreCriteriaType, ref: CriterionRef): string =>
	groupOf(scoring, ref)?.ifNotFound ?? "Ignore";

const withCriteria = (
	scoring: ScoreCriteriaType,
	update: (criteria: ScoreCriteriaCriterion) => ScoreCriteriaCriterion
): ScoreCriteriaType => ({ ...scoring, criteria: update(scoring.criteria) });

const patchGroup = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef,
	patch: Partial<Group>
): ScoreCriteriaType =>
	withCriteria(scoring, (criteria) => {
		if (ref.key === "OTHER") {
			return {
				...criteria,
				[ref.section]: {
					...criteria[ref.section],
					OTHER: criteria[ref.section].OTHER.map((other, index) =>
						index === ref.index ? { ...other, ...patch } : other
					),
				},
			};
		}

		const section = criteria[ref.section] as unknown as Record<string, Group>;

		return {
			...criteria,
			[ref.section]: { ...section, [ref.key]: { ...(section[ref.key] ?? {}), ...patch } },
		};
	});

export const setCriterionOn = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef,
	active: boolean
): ScoreCriteriaType => patchGroup(scoring, ref, { active });

export const setIfNotFound = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef,
	ifNotFound: "Ignore" | "Disqualified"
): ScoreCriteriaType => patchGroup(scoring, ref, { ifNotFound });

/* ---------- scorings ---------- */

const emptyGroup = () => ({ active: false, ifNotFound: "Ignore", open: false, items: [] });

/** A rubric with every criterion present and switched off. */
export const emptyCriteriaJson = (): ScoreCriteriaCriterion => ({
	companyScoring: {
		HQ_COUNTRY: emptyGroup(),
		SUBSIDIARY_COUNTRY: emptyGroup(),
		EMPLOYEE_SIZE: emptyGroup(),
		INDUSTRY: emptyGroup(),
		OTHER: [],
	},
	personaScoring: {
		JOB_LEVEL: emptyGroup(),
		PERSON_LOCATION: emptyGroup(),
		OTHER: [],
	},
});

export const newScoring = (name: string, id: number = tempId()): ScoreCriteriaType => ({
	id,
	name,
	criteria: emptyCriteriaJson(),
	scoreCriteriaCompaniesPersonas: [],
	locked: true,
	decisionMakerWithoutClearFunction: false,
	updatedById: null,
	updatedBy: null,
	updatedAt: null,
	collapsed: false,
});

/** A copy of a scoring's grades under a new name; it starts with no cells of its own. */
export const duplicateScoring = (
	source: ScoreCriteriaType,
	name: string,
	id: number = tempId()
): ScoreCriteriaType => ({
	...newScoring(name, id),
	criteria: JSON.parse(JSON.stringify(source.criteria)) as ScoreCriteriaCriterion,
});

export const renameScoring = (scoring: ScoreCriteriaType, name: string): ScoreCriteriaType => ({
	...scoring,
	name,
});

/** `locked` is what the engine reads as "active" — an unlocked scoring grades nothing. */
export const setScoringActive = (
	scoring: ScoreCriteriaType,
	active: boolean
): ScoreCriteriaType => ({
	...scoring,
	locked: active,
});

/* ---------- seniority ladder (fixed vocabulary) ---------- */

export type FixedTiers = Record<Tier, string[]>;

const emptyTiers = (): FixedTiers => ({ 3: [], 2: [], 1: [], 0: [] });

export const SENIORITY_POOL: readonly string[] = SCORABLE_SENIORITY_LEVELS;

export const readSeniorityTiers = (scoring: ScoreCriteriaType): FixedTiers => {
	const tiers = emptyTiers();

	for (const item of scoring.criteria.personaScoring.JOB_LEVEL?.items ?? []) {
		const tier = TIERS.find((entry) => entry === item.points);

		if (tier !== undefined) {
			tiers[tier].push(
				...(item.seniorityLevel ?? []).filter((level) => !tiers[tier].includes(level))
			);
		}
	}

	return tiers;
};

/** One item per tier that has values, in display order. */
export const writeSeniorityTiers = (
	scoring: ScoreCriteriaType,
	tiers: FixedTiers
): ScoreCriteriaType =>
	withCriteria(scoring, (criteria) => ({
		...criteria,
		personaScoring: {
			...criteria.personaScoring,
			JOB_LEVEL: {
				...criteria.personaScoring.JOB_LEVEL,
				items: TIERS.filter((tier) => tiers[tier].length > 0).map((tier) => ({
					points: tier,
					seniorityLevel: tiers[tier],
					description: "",
				})),
			},
		},
	}));

/** Moves a value into `tier`, or out of it when it is already there. */
export const toggleFixedValue = (tiers: FixedTiers, tier: Tier, value: string): FixedTiers => {
	const next = emptyTiers();

	for (const entry of TIERS) {
		next[entry] = tiers[entry].filter((current) => current !== value);
	}

	if (!tiers[tier].includes(value)) {
		next[tier] = [...next[tier], value];
	}

	return next;
};

export const tierOfFixedValue = (tiers: FixedTiers, value: string): Tier | null =>
	TIERS.find((tier) => tiers[tier].includes(value)) ?? null;

/** The pool values no tier lists yet. */
export const unassignedFixedValues = (pool: readonly string[], tiers: FixedTiers): string[] =>
	pool.filter((value) => tierOfFixedValue(tiers, value) === null);

/* ---------- AI criteria ---------- */

export const TWO_BUCKET_POINTS: readonly Tier[] = [3, 0];

/**
 * A criterion created by the redesign has exactly the "score higher" (3) and
 * "disqualify" (0) buckets; anything carrying a 1- or 2-point bucket is a legacy
 * four-bucket rubric and keeps the four-textarea editor.
 */
export const isTwoBucketAi = (other: Pick<AiCriterion, "criteria">): boolean =>
	other.criteria.every((bucket) => TWO_BUCKET_POINTS.includes(bucket.points as Tier));

export const addAiCriterion = (
	scoring: ScoreCriteriaType,
	section: Section,
	criteriaName: string
): { scoring: ScoreCriteriaType; ref: CriterionRef } => {
	const other: AiCriterion = {
		criteriaName,
		active: true,
		ifNotFound: "Ignore",
		open: false,
		criteria: TWO_BUCKET_POINTS.map((points) => ({ points, type: "OTHER", description: "" })),
	};
	const next = withCriteria(scoring, (criteria) => ({
		...criteria,
		[section]: { ...criteria[section], OTHER: [...criteria[section].OTHER, other] },
	}));

	return {
		scoring: next,
		ref: { section, key: "OTHER", index: scoring.criteria[section].OTHER.length },
	};
};

export const removeAiCriterion = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef
): ScoreCriteriaType =>
	withCriteria(scoring, (criteria) => ({
		...criteria,
		[ref.section]: {
			...criteria[ref.section],
			OTHER: criteria[ref.section].OTHER.filter((_, index) => index !== ref.index),
		},
	}));

export const renameAiCriterion = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef,
	criteriaName: string
): ScoreCriteriaType => patchGroup(scoring, ref, { criteriaName } as Partial<Group>);

export const readAiText = (scoring: ScoreCriteriaType, ref: CriterionRef, points: Tier): string =>
	aiCriterionOf(scoring, ref)?.criteria.find((bucket) => bucket.points === points)?.description ??
	"";

/** Writes one bucket's text, creating the bucket when the criterion did not have it. */
export const writeAiText = (
	scoring: ScoreCriteriaType,
	ref: CriterionRef,
	points: Tier,
	description: string
): ScoreCriteriaType => {
	const other = aiCriterionOf(scoring, ref);

	if (!other) {
		return scoring;
	}

	const exists = other.criteria.some((bucket) => bucket.points === points);
	const buckets = exists
		? other.criteria.map((bucket) =>
				bucket.points === points ? { ...bucket, description } : bucket
			)
		: [...other.criteria, { points, type: "OTHER", description }];

	return patchGroup(scoring, ref, { criteria: buckets } as Partial<Group>);
};

/* ---------- copying ---------- */

/** Replaces one criterion's grades with another scoring's grades for the same criterion. */
export const copyCriterionFrom = (
	target: ScoreCriteriaType,
	source: ScoreCriteriaType,
	ref: CriterionRef
): ScoreCriteriaType => {
	if (ref.key === "OTHER") {
		const name = aiCriterionOf(target, ref)?.criteriaName;
		const match = source.criteria[ref.section].OTHER.find((other) => other.criteriaName === name);

		return match
			? patchGroup(target, ref, {
					criteria: JSON.parse(JSON.stringify(match.criteria)),
				} as Partial<Group>)
			: target;
	}

	const group = (source.criteria[ref.section] as unknown as Record<string, Group>)[ref.key];

	return group
		? patchGroup(target, ref, JSON.parse(JSON.stringify(group)) as Partial<Group>)
		: target;
};

/** Replaces every grade of the target with the source's. */
export const copyAllFrom = (
	target: ScoreCriteriaType,
	source: ScoreCriteriaType
): ScoreCriteriaType => ({
	...target,
	criteria: JSON.parse(JSON.stringify(source.criteria)) as ScoreCriteriaCriterion,
});

/** Copies the company HQ buckets and if-not-found onto the person location. */
export const copyHqToPersonLocation = (scoring: ScoreCriteriaType): ScoreCriteriaType =>
	withCriteria(scoring, (criteria) => ({
		...criteria,
		personaScoring: {
			...criteria.personaScoring,
			PERSON_LOCATION: {
				...criteria.personaScoring.PERSON_LOCATION,
				ifNotFound: criteria.companyScoring.HQ_COUNTRY.ifNotFound,
				items: criteria.companyScoring.HQ_COUNTRY.items.map((item) => ({
					...item,
					countries: [...(item.countries ?? [])],
				})),
			},
		},
	}));

/* ---------- change detection ---------- */

/** Free-text grades: locations mix countries, regions and the catch-all; industries are typed. */
const CONFLICT_KEYS: FixedCriterionKey[] = [
	"HQ_COUNTRY",
	"SUBSIDIARY_COUNTRY",
	"INDUSTRY",
	"PERSON_LOCATION",
];

/** Whether a grade the conflict check reads changed — the check is skipped otherwise. */
export const conflictCriteriaChanged = (
	before: ScoreCriteriaType | undefined,
	after: ScoreCriteriaType
): boolean =>
	!before ||
	CONFLICT_KEYS.some((key) => {
		const section: Section = key === "PERSON_LOCATION" ? "personaScoring" : "companyScoring";
		const read = (scoring: ScoreCriteriaType) =>
			JSON.stringify(
				(scoring.criteria[section] as unknown as Record<string, Group>)[key]?.items ?? []
			);

		return read(before) !== read(after);
	});

const editable = (scoring: ScoreCriteriaType) =>
	JSON.stringify({
		name: scoring.name,
		criteria: scoring.criteria,
		locked: scoring.locked,
		decisionMakerWithoutClearFunction: scoring.decisionMakerWithoutClearFunction,
	});

/** Whether the page edited the scoring — cells and audit fields do not count. */
export const isScoringChanged = (
	before: ScoreCriteriaType | undefined,
	after: ScoreCriteriaType
): boolean => !before || editable(before) !== editable(after);

const VALUE_FIELDS = ["countries", "industries", "seniorityLevel"];

/**
 * How many grades a source scoring holds for the criterion the copy menu is
 * about: definitions for an AI criterion (matched by name; null when the
 * source has none of that name), otherwise the values across all buckets.
 */
export const copySourceCount = (
	target: ScoreCriteriaType,
	source: ScoreCriteriaType,
	ref: CriterionRef
): number | null => {
	if (ref.key === "OTHER") {
		const name = aiCriterionOf(target, ref)?.criteriaName;
		const match = source.criteria[ref.section].OTHER.find((other) => other.criteriaName === name);

		return match ? match.criteria.filter((bucket) => bucket.description.trim()).length : null;
	}

	const items = (groupOf(source, ref)?.items ?? []) as Record<string, unknown>[];

	if (ref.key === "EMPLOYEE_SIZE") {
		return items.length;
	}

	return items.reduce(
		(count, item) =>
			count +
			VALUE_FIELDS.reduce(
				(sum, field) => sum + (Array.isArray(item[field]) ? (item[field] as string[]).length : 0),
				0
			),
		0
	);
};
